// ── Docs search index ───────────────────────────────────────────────────────
// Built from the same registry the pages render from, plus the OpenAPI spec
// (so every REST operation is findable). Sections split on h2/h3 — a result
// deep-links to /docs/<page>#<heading>.

import { DOC_PAGES, docHref, slugifyHeading, type DocPageDef } from './content'
import { OPENAPI_SPEC } from '../api/openapi'

export interface SearchEntry {
  href: string
  page: string
  heading: string | null
  text: string
  /** lowercase haystacks, precomputed */
  hayTitle: string
  hayHeading: string
  hayText: string
}

function sectionsOf(page: DocPageDef): SearchEntry[] {
  const entries: SearchEntry[] = []
  const base = docHref(page)
  let heading: string | null = null
  let id = ''
  let buf: string[] = []
  let inFence = false

  const flush = () => {
    const text = buf
      .join(' ')
      .replace(/\[([^\]]+)\]\([^)\s]+\)/g, '$1') // [label](href) → label
      .replace(/[`*_>|#-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    entries.push({
      href: heading ? `${base}#${id}` : base,
      page: page.title,
      heading,
      text: text.slice(0, 220),
      hayTitle: page.title.toLowerCase(),
      hayHeading: (heading ?? '').toLowerCase(),
      hayText: text.toLowerCase(),
    })
    buf = []
  }

  // The page intro (before the first heading) is a section of its own,
  // seeded with the description so short queries hit page-level results.
  buf.push(page.description)

  for (const line of page.body.split('\n')) {
    if (line.trimStart().startsWith('```')) {
      inFence = !inFence
      continue
    }
    if (inFence) {
      buf.push(line)
      continue
    }
    const m = line.match(/^(#{2,3})\s+(.*)/)
    if (m) {
      flush()
      heading = m[2].replace(/[`*]/g, '').trim()
      id = slugifyHeading(heading)
      continue
    }
    if (line.trim() && !line.startsWith(':::')) buf.push(line)
  }
  flush()
  return entries
}

function apiEntries(): SearchEntry[] {
  const out: SearchEntry[] = []
  const paths = OPENAPI_SPEC.paths as Record<string, Record<string, { summary?: string; description?: string }>>
  for (const [path, ops] of Object.entries(paths)) {
    for (const [method, op] of Object.entries(ops)) {
      const heading = `${method.toUpperCase()} ${path}`
      const text = [op.summary, op.description].filter(Boolean).join(' — ')
      out.push({
        href: '/docs/api#endpoint-reference',
        page: 'REST API',
        heading,
        text: text.slice(0, 220),
        hayTitle: 'rest api',
        hayHeading: heading.toLowerCase(),
        hayText: text.toLowerCase(),
      })
    }
  }
  return out
}

let cached: SearchEntry[] | null = null
export function searchIndex(): SearchEntry[] {
  if (!cached) cached = [...DOC_PAGES.flatMap(sectionsOf), ...apiEntries()]
  return cached
}

export interface SearchHit extends SearchEntry {
  score: number
}

export function searchDocs(query: string, limit = 12): SearchHit[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (!terms.length) return []
  const hits: SearchHit[] = []
  for (const e of searchIndex()) {
    let score = 0
    for (const t of terms) {
      if (e.hayTitle.includes(t)) score += 5
      if (e.hayHeading.includes(t)) score += 4
      if (e.hayText.includes(t)) score += 1
      if (e.hayHeading.startsWith(t)) score += 2
    }
    // Every term must appear somewhere.
    const allMatch = terms.every(
      (t) => e.hayTitle.includes(t) || e.hayHeading.includes(t) || e.hayText.includes(t),
    )
    if (allMatch && score > 0) hits.push({ ...e, score })
  }
  return hits.sort((a, b) => b.score - a.score).slice(0, limit)
}
